/**
 * Phase 7: acting admin opens email link — confirm team member removal (no auth required).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TEAM_REMOVAL_CONFIRM_UI } from '../constants/accountCancellationWorkflow';
import {
  fetchTeamRemovalConfirmTokenState,
  postTeamRemovalConfirmComplete,
  type TeamRemovalTokenPublicState,
} from '../services/teamRemovalConfirmApi';

export default function ConfirmTeamRemoval(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const token = searchParams.get(TEAM_REMOVAL_CONFIRM_UI.QUERY_PARAM) || '';
  const [tokenState, setTokenState] = useState<TeamRemovalTokenPublicState | 'loading'>('loading');
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!token.trim()) {
      setTokenState('invalid');
      return;
    }
    let cancelled = false;
    void (async () => {
      const s = await fetchTeamRemovalConfirmTokenState(token);
      if (!cancelled) {
        setTokenState(s);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onConfirm = useCallback(async () => {
    if (!token.trim()) return;
    setConfirming(true);
    setActionError(null);
    const result = await postTeamRemovalConfirmComplete(token);
    setConfirming(false);
    if (result.ok) {
      setTokenState('completed');
      return;
    }
    setActionError(result.message);
  }, [token]);

  const body = useMemo(() => {
    switch (tokenState) {
      case 'loading':
        return 'Checking your link…';
      case 'pending':
        return 'This link is valid. Confirm below to remove the team member from your organization. This matches the removal you started in Settings.';
      case 'completed':
        return 'Removal is complete. They no longer have access to this company in Tranzit.';
      case 'expired':
        return 'This link has expired. Start again from Settings → Security → Team directory if you still need to remove someone.';
      case 'not_available':
        return 'This confirmation is no longer active. If the member was already removed, no further action is needed.';
      case 'invalid':
      default:
        return 'This link is invalid or incomplete. Use the link from your email or contact support.';
    }
  }, [tokenState]);

  const showConfirm =
    tokenState === 'pending' && !confirming && token.trim().length > 0;

  return (
    <div
      style={{
        minHeight: '40vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        fontFamily: 'system-ui, sans-serif',
        maxWidth: 520,
        margin: '0 auto',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Confirm team member removal</h1>
      <p style={{ lineHeight: 1.5, color: '#333' }}>{body}</p>
      {showConfirm ? (
        <button
          type="button"
          onClick={() => void onConfirm()}
          style={{
            marginTop: '1.5rem',
            padding: '0.6rem 1.25rem',
            fontSize: '1rem',
            cursor: 'pointer',
            borderRadius: 6,
            border: '1px solid #333',
            background: '#1a1a1a',
            color: '#fff',
          }}
        >
          Confirm removal
        </button>
      ) : null}
      {confirming ? (
        <p style={{ marginTop: '1rem', color: '#555' }}>Processing…</p>
      ) : null}
      {actionError ? (
        <p style={{ marginTop: '1rem', color: '#b00020' }} role="alert">
          {actionError}
        </p>
      ) : null}
    </div>
  );
}
